import React, { forwardRef, useContext, useImperativeHandle, useRef, useMemo } from 'react'
import Pagination, { defaultProps } from './Pagination';
import { PaginationProps, PaginationAdapterRef } from './iPagination';
import { setComponentSize } from "../../wui-core/src/componentStyle"
import { isShouldUpdate } from '../../wui-core/src/warning';
import { ConfigContext } from '../../wui-provider/src/context';
import { globalConfig } from "../../wui-provider/src";

// 使用 forwardRef 和 useImperativeHandle 处理 ref 转发
const PaginationAdapter = forwardRef<PaginationAdapterRef, Partial<PaginationProps>>((props, ref) => {
    const {
        // 当前页
        current,
        activePage,
        // 分页改变事件
        onChange,
        onSelect,
        // 是否展示快速跳页
        showQuickJumper,
        showJump,
        // 页码数组
        pageSizeOptions: propPageSizeOptions = defaultProps.pageSizeOptions,
        dataNumSelect,
        // 页码改变事件
        onShowSizeChange,
        onPageSizeChange,
        onDataNumSelect,
        size = 'sm',
        // 默认当前页
        defaultCurrent,
        defaultActivePage,
        // 默认页码
        defaultPageSize: propDefaultPageSize,
        defaultDataNum,
        pageSize: propPageSize,
        dataNum,
        locale: propsLocale,
        disabled: propsDisabled,
        ...others
    } = props;

    const context = useContext(ConfigContext) // 接受provider控制
    const globalLocale = globalConfig().getGlobalLocale();
    const locale = propsLocale || context.locale || globalLocale || 'zh-cn'
    const disabled = propsDisabled || context.disabled

    // 使用 useRef 保存 Pagination 组件实例引用
    const paginationRef = useRef<typeof Pagination | null>(null);

    // 使用 useImperativeHandle 暴露组件方法，符合 .cursorrules.md 第 7 条引用处理规范
    useImperativeHandle(ref, () => ({
        getCache: () => {
            return paginationRef.current?.getCache();
        }
    }), []);

    // 检查是否需要更新
    isShouldUpdate("Pagination", props);

    // 使用 useMemo 优化页码选项的计算逻辑，避免不必要的重新计算
    const { pageSizeOptions, pageSize, defaultPageSize } = useMemo(() => {
        let pageSizeOptions = propPageSizeOptions ?? dataNumSelect ?? [];
        // 将下拉数组 页码大小都转成数字类型
        pageSizeOptions = pageSizeOptions.filter(num => num && !isNaN(Number(num))).map(num => Number(num));

        let pageSize = propPageSize && Number(propPageSize);
        let defaultPageSize = propDefaultPageSize && Number(propDefaultPageSize);

        // 如果dataNumSelect里没有当前指定的页码，则将pageSize加入到dataNumSelect，并不可修改
        if (pageSize && !pageSizeOptions.includes(pageSize)) {
            pageSizeOptions.push(pageSize)
            pageSizeOptions = pageSizeOptions.sort((a: any, b: any) => a - b)
        } else if (defaultPageSize && !pageSizeOptions.includes(defaultPageSize)) {
            pageSizeOptions.push(defaultPageSize)
            pageSizeOptions = pageSizeOptions.sort((a: any, b: any) => a - b)
        }

	    if (defaultDataNum && !isNaN(defaultDataNum) && !defaultPageSize && pageSizeOptions[defaultDataNum]) {
            defaultPageSize = pageSizeOptions[defaultDataNum] as any
        }

	    if (dataNum && !isNaN(dataNum) && !pageSize && pageSizeOptions[dataNum]) {
            pageSize = pageSizeOptions[dataNum] as any
        }

        return { pageSizeOptions, pageSize, defaultPageSize };
    }, [propPageSizeOptions, dataNumSelect, propPageSize, propDefaultPageSize, defaultDataNum, dataNum]);

    // 保持 DOM 结构不变，与原 class 组件相同
    return (
        <Pagination
            ref={paginationRef}
            locale={locale}
            disabled={disabled}
            showQuickJumper={showQuickJumper ?? showJump}
            activePage={current || activePage}
            defaultActivePage={defaultCurrent || defaultActivePage}
            pageSize={pageSize} // 指定的分页数量
            defaultPageSize={defaultPageSize}
            pageSizeOptions={pageSizeOptions}
            size={setComponentSize(size) as any}
            onChange={onChange || onSelect}
            onPageSizeChange={onPageSizeChange || onShowSizeChange || onDataNumSelect}
            {...others}
        />
    )
});

// 设置组件显示名称，便于调试
PaginationAdapter.displayName = 'PaginationAdapter';

// 添加静态方法，保持 API 兼容性
(PaginationAdapter as any).getCache = Pagination.getCache;

export default PaginationAdapter;